import { supabase } from '@/lib/supabase';
import type { AccessLevel, Profile, ProfileAccess } from '@/types/db';

export interface ProfileAccessWithProfile extends ProfileAccess {
  profile: Pick<Profile, 'id' | 'name' | 'avatar_color' | 'relation'>;
}

/**
 * Lists profile_access rows visible to the caller. RLS on `profile_access`
 * already filters this to: (a) rows that belong to me, and (b) rows on
 * profiles I own. The Settings screen treats both cases:
 *   - case (a): profiles I have access to (shows me and my co-caregivers)
 *   - case (b): profiles I own  (shows me + invitees)
 */
export async function listProfileAccess(): Promise<ProfileAccessWithProfile[]> {
  const { data, error } = await supabase
    .from('profile_access')
    .select(
      `
        id, profile_id, user_id, invited_email, access_level,
        accepted_at, created_at,
        profile:profiles!inner ( id, name, avatar_color, relation )
      `,
    )
    .order('created_at', { ascending: true });
  if (error) throw error;

  type Row = ProfileAccess & {
    profile:
      | Pick<Profile, 'id' | 'name' | 'avatar_color' | 'relation'>
      | Pick<Profile, 'id' | 'name' | 'avatar_color' | 'relation'>[]
      | null;
  };
  return ((data ?? []) as Row[])
    .map((row) => {
      const profile = Array.isArray(row.profile) ? row.profile[0] : row.profile;
      if (!profile) return null;
      return { ...row, profile } as ProfileAccessWithProfile;
    })
    .filter((row): row is ProfileAccessWithProfile => row !== null);
}

export async function revokeProfileAccess(accessId: string): Promise<void> {
  // Use .select() so RLS-silent zero-row matches surface as an error.
  const { data, error } = await supabase
    .from('profile_access')
    .delete()
    .eq('id', accessId)
    .select('id');
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error('revoke_no_match');
  }
}

export interface InviteInput {
  profileId: string;
  email: string;
  accessLevel: Exclude<AccessLevel, 'owner'>;
}

export async function inviteCaregiver(
  input: InviteInput,
): Promise<ProfileAccess> {
  const normalizedEmail = input.email.trim().toLowerCase();
  if (!normalizedEmail) throw new Error('empty_email');
  const { data, error } = await supabase
    .from('profile_access')
    .insert({
      profile_id: input.profileId,
      invited_email: normalizedEmail,
      user_id: null,
      access_level: input.accessLevel,
    })
    .select()
    .single();
  if (error) {
    // Unique constraint on (profile_id, invited_email) where user_id is null
    if (error.code === '23505') throw new Error('duplicate_invite');
    throw error;
  }
  return data as ProfileAccess;
}
